import { ReactNode } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import Button from '../../../components/Forms/Button';
import { Link } from '../../../components/Link';
import { ChevronIcon } from '../../../images/icons/chevron';
import { City } from '../../../services/cities/types';
import { screens } from '../../../styles/constants';
import { createStay22Url } from '../../../utils/stay22';
import {
  FIND_MY_TICKETS_SECTION_ID,
  PRICE_CALENDAR_SECTION_ID,
  TRIP_INFORMATION_SECTION_ID,
} from '../constants';
import { canDisplayTripInformationSection } from '../utils';
import messages from './messages';
import SearchViewContent from './SearchViewContent';

const GridContainer = styled.div`
  ${tw`mt-huge2`}

  ${tw`px-medium2`}

  @media (min-width: ${screens.md}) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: var(--size-medium2);
    grid-row-gap: var(--size-medium2);
  }
`;

const generateMenuLinks = (tripData): Array<MenuLink> => {
  const links: Array<MenuLink> = [
    {
      id: PRICE_CALENDAR_SECTION_ID,
      children: <FormattedHTMLMessage {...messages.priceCalendar} />,
    },
    {
      id: FIND_MY_TICKETS_SECTION_ID,
      children: <FormattedHTMLMessage {...messages.findMyTickets} />,
    },
  ];

  if (canDisplayTripInformationSection(tripData)) {
    links.push({
      id: TRIP_INFORMATION_SECTION_ID,
      children: <FormattedHTMLMessage {...messages.tripInfo} />,
    });
  }

  return links;
};

const SearchView2 = (props) => {
  return (
    <div>
      <GridContainer>
        <Menu
          destinationCity={props.destinationCity}
          links={generateMenuLinks(props.tripData)}
        />
        <SearchViewContent {...props} />
      </GridContainer>

      {props.legacyExtraContent}
    </div>
  );
};

const MenuContainer = styled.div`
  ${tw`hidden md:block relative`}

  @media (min-width: ${screens.md}) {
    grid-column-start: 1;
    grid-column-end: 5;
  }

  @media (min-width: ${screens.lg}) {
    grid-column-start: 2;
  }

  @media (min-width: ${screens.xl}) {
    grid-column-end: 4;
  }
`;

const MenuContent = styled.div`
  ${tw`py-huge px-medium sticky shadow-medium rounded-30 flex flex-col`}
  gap: var(--size-medium);
  top: var(--size-medium);
`;

const LinkList = styled.ul`
  ${tw`flex flex-col px-medium m-0 `}
  gap: var(--size-medium);
`;

const LinkStyle = css`
  ${tw`text-p-small leading-p-small font-bold`}
`;

type MenuProps = {
  links: Array<MenuLink>;
  destinationCity: City;
};

type MenuLink = {
  children: ReactNode;
  id: string;
};

const Menu = (props: MenuProps) => {
  const { destinationCity, links } = props;

  return (
    <MenuContainer>
      <MenuContent>
        <LinkList>
          {links.map((link) => (
            <li key={link.id}>
              <Link customStyle={LinkStyle} href={`#${link.id}`}>
                {link.children}
              </Link>
            </li>
          ))}

          <li>
            <Link
              customStyle={LinkStyle}
              href={createStay22Url({
                campaign: 'search_page',
                address: destinationCity.name,
              })}
              target="_blank"
            >
              <FormattedMessage
                {...messages.findHotel}
                values={{ destinationCity: destinationCity.name }}
              />
            </Link>
          </li>
        </LinkList>

        <Button
          onClick={() => window.scrollTo(0, 0)}
          rightIcon={
            <ChevronIcon
              customStyle={css`
                transform: rotate(180deg);
                width: 9px;
                height: 9px;
              `}
            />
          }
          size="SMALL"
          variant="PRIMARY"
        >
          <FormattedMessage {...messages.searchMyTrip} />
        </Button>
      </MenuContent>
    </MenuContainer>
  );
};

export default SearchView2;
